import { createFileRoute } from "@tanstack/react-router";
import { createFormData, useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { pick, unPick } from "@/lib/utils";
import type { ImageFile } from "@/components/ui/form";
import type { Asset } from "@/store/Asset";
import type { Service } from "@/store/Service";
import { ServiceForm } from "@/components/service/service-form";
import BackButton from "@/components/ui/shared/back-button";

export const Route = createFileRoute("/(dasboard)/products/services/$id")({
  component: EditProductPage,
});

function EditProductPage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const params = Route.useParams();
  const service = store.service.current as Service;

  const form = useSimpleForm({
    images: [],
    featured_image: undefined,
  });
  const [currentImages, setCurrentImages] = useState<ImageFile[]>([]);

  useEffect(() => {
    const id = params.id as string;
    if (id) {
      store.service
        .fetchOne(id)
        .then((service) => {
          store.service.setCurrent(service);
          initData(service);
        })
        .catch((error: any) => {
          store.errors.catch(error);
        });
    }
  }, [params.id]);

  const initData = (service: Service) => {
    form.setValues((values: any) => ({
      ...values,
      name: service.name,
      description: service.description,
      category_id: service.category_id,
      images: [],
    }));

    form.setValues((values: any) => ({
      ...values,
      images: [],
      ...pick(service, [
        "name",
        "description",
        "category_id",
        "selling_price",
        "unit_price",
      ]),
    }));

    if (service.featured_image) {
      form.setValue("featured_image", {
        file: undefined,
        src: service.featured_image?.url,
        id: service.featured_image.id,
      });
    }

    setCurrentImages(
      service.images.map((item: Asset) => ({
        file: undefined,
        src: item.url,
        id: item.id,
      })) || []
    );
  };

  const submit = async () => {
    store.errors.reset();
    if (!service) {
      return;
    }
    const data = unPick(form.values, ["featured_image"]);
    data["featured_image_id"] = service.featured_image?.id;

    // If the featured changed.
    if (
      service.featured_image?.id != form.values.featured_image?.id &&
      form.values.featured_image
    ) {
      // If a new image is uploaded for the featured image. Then we same its index.
      if (form.values.featured_image.file) {
        for (let i = 0; i < form.values.images.length; i++) {
          if (form.values.images[i].id == form.values.featured_image.id)
            data["featured_image_index"] = i;
        }
      } else {
        // otherwise we submit the id of the image selected.
        data["featured_image_id"] = currentImages.find(
          (item) => item.id == form.values.featured_image.id
        )?.id;
      }
    }

    // Images removed are one that where present at the beginning so store inside product.images and that have been deleted
    data["images_removed_ids"] = service.images
      .filter(
        (image) => !currentImages?.map((item) => item.src).includes(image.url)
      )
      .map((image) => image.id);

    store.service
      .updateCurrent(createFormData(data))
      .then(() => {
        store.service.fetch({});
        store.display.show("edit_product_success");
      })
      .catch((error) => {
        store.errors.catch(error);
      })
      .finally(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Alert
        autoClose={3000}
        message={t("Service edit with success")}
        name={"edit_product_success"}
      />

      <BackButton />

      <div className="my-4 flex justify-between">
        <h2 className=" text-xl font-semibold">{t("Edit service")}</h2>
        <Button
          className="px-6 py-2"
          onClick={submit}
          loading={store.service.loading("update")}
        >
          {t("Save changes")}
        </Button>
      </div>

      <div>{store.errors.render()}</div>

      {service && (
        <ServiceForm
          mode="edit"
          form={form}
          currentImages={currentImages}
          setCurrentImages={setCurrentImages}
        />
      )}

      <Button
        className="mt-8 px-6 py-3"
        onClick={submit}
        loading={store.service.loading("update")}
      >
        {t("Save changes")}
      </Button>
    </div>
  );
}
